import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

interface ProductField {
  key: string;
  label: string;
  required: boolean;
  example: string;
}

export const PRODUCT_CSV_FIELDS: ProductField[] = [
  { key: "product_name", label: "Product Name", required: true, example: "Wireless Bluetooth Headphones Pro X200" },
  { key: "product_description", label: "Description", required: true, example: "Premium wireless headphones with active noise cancellation" },
  { key: "category", label: "Category", required: false, example: "Electronics" },
  { key: "brand", label: "Brand", required: false, example: "SoundTech" },
  { key: "price", label: "Price", required: false, example: "299.99" },
  { key: "features", label: "Key Features", required: false, example: "Bluetooth 5.3, 40h battery, ANC" },
  { key: "inventory_status", label: "Inventory Status", required: false, example: "In Stock" },
  { key: "product_url", label: "Product URL", required: false, example: "https://example.com/product" },
];

const CSV_HEADER_MAP: Record<string, string> = {
  "product name": "product_name",
  "product_name": "product_name",
  "name": "product_name",
  "product description": "product_description",
  "description": "product_description",
  "product_description": "product_description",
  "category": "category",
  "brand": "brand",
  "price": "price",
  "key features": "features",
  "features": "features",
  "key_features": "features",
  "inventory status": "inventory_status",
  "inventory_status": "inventory_status",
  "stock": "inventory_status",
  "product url": "product_url",
  "product_url": "product_url",
  "url": "product_url",
  "link": "product_url",
};

export interface ImportError {
  row: number;
  message: string;
}

export interface ImportResult {
  rowsRead: number;
  imported: number;
  failed: number;
  errors: ImportError[];
  products: Record<string, string>[];
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        result.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
  }
  result.push(current.trim());
  return result;
}

function mapHeaderToKey(header: string): string | null {
  const normalized = header.trim().toLowerCase();
  return CSV_HEADER_MAP[normalized] || null;
}

function isEmptyRow(values: Record<string, string>): boolean {
  return Object.values(values).every((v) => !v.trim());
}

function isDuplicate(
  values: Record<string, string>,
  existing: Record<string, string>[]
): boolean {
  const name = values.product_name?.trim().toLowerCase();
  if (!name) return false;
  return existing.some(
    (p) => p.product_name?.trim().toLowerCase() === name
  );
}

interface CsvProductImporterProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (products: Record<string, string>[]) => void;
  existingProductNames: string[];
}

export default function CsvProductImporter({
  isOpen,
  onClose,
  onImport,
  existingProductNames,
}: CsvProductImporterProps) {
  const { t } = useTranslation("dashboard");
  const [step, setStep] = useState<"upload" | "validating" | "results">("upload");
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setStep("upload");
    setDragOver(false);
    setResult(null);
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const generateSampleCsv = () => {
    const header = PRODUCT_CSV_FIELDS.map((f) => f.label).join(",");
    const row1 = PRODUCT_CSV_FIELDS.map((f) => f.example).join(",");
    const row2 = "Smart Watch Ultra,Advanced fitness tracking smartwatch with GPS,Wearables,TechFit,199.99,Heart rate monitor, IP68 waterproof, 7-day battery,In Stock,https://example.com/watch";
    const bom = "\uFEFF";
    const content = `${bom}${header}\n${row1}\n${row2}`;
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = t("csvImporter.downloadTemplate").replace(/ /g, "_") + ".csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const processFile = (file: File) => {
    if (!file.name.endsWith(".csv")) {
      setResult({
        rowsRead: 0,
        imported: 0,
        failed: 1,
        errors: [{ row: 0, message: t("csvImporter.invalidFileType") }],
        products: [],
      });
      setStep("results");
      return;
    }

    setStep("validating");

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const cleanText = text.replace(/^\uFEFF/, "");
      const lines = cleanText.split(/\r?\n/).filter((line) => line.trim());

      if (lines.length < 2) {
        setResult({
          rowsRead: 0,
          imported: 0,
          failed: 1,
          errors: [{ row: 0, message: t("csvImporter.csvHeadersOnly") }],
          products: [],
        });
        setStep("results");
        return;
      }

      const headerLine = lines[0];
      const headers = parseCSVLine(headerLine);

      const columnMap: (string | null)[] = headers.map(mapHeaderToKey);
      const mappedKeys = columnMap.filter((k): k is string => k !== null);

      if (mappedKeys.length === 0) {
        setResult({
          rowsRead: 0,
          imported: 0,
          failed: 1,
          errors: [{ row: 0, message: t("csvImporter.noHeaderColumns") }],
          products: [],
        });
        setStep("results");
        return;
      }

      if (!mappedKeys.includes("product_name") || !mappedKeys.includes("product_description")) {
        setResult({
          rowsRead: 0,
          imported: 0,
          failed: 1,
          errors: [{ row: 0, message: t("csvImporter.missingRequiredCols") }],
          products: [],
        });
        setStep("results");
        return;
      }

      const dataLines = lines.slice(1);
      const errors: ImportError[] = [];
      const imported: Record<string, string>[] = [];
      const existingLower = existingProductNames.map((n) => n.trim().toLowerCase());

      dataLines.forEach((line, idx) => {
        const rowNum = idx + 2;
        const values = parseCSVLine(line);
        const rowData: Record<string, string> = {};

        columnMap.forEach((key, colIdx) => {
          if (key) {
            rowData[key] = (values[colIdx] || "").trim();
          }
        });

        if (isEmptyRow(rowData)) {
          return;
        }

        const name = rowData.product_name;
        if (!name) {
          errors.push({ row: rowNum, message: t("csvImporter.missingName") });
          return;
        }

        if (!rowData.product_description) {
          errors.push({ row: rowNum, message: t("csvImporter.missingDesc", { name }) });
          return;
        }

        if (rowData.price && rowData.price.trim()) {
          const priceVal = rowData.price.replace(/[^0-9.]/g, "");
          if (priceVal === "" || isNaN(parseFloat(priceVal)) || parseFloat(priceVal) < 0) {
            errors.push({ row: rowNum, message: t("csvImporter.invalidPrice", { name, price: rowData.price }) });
            return;
          }
        }

        if (existingLower.includes(name.toLowerCase())) {
          errors.push({ row: rowNum, message: t("csvImporter.duplicate", { name }) });
          return;
        }

        const dupInBatch = imported.some(
          (p) => p.product_name?.trim().toLowerCase() === name.toLowerCase()
        );
        if (dupInBatch) {
          errors.push({ row: rowNum, message: t("csvImporter.dupInCsv", { name }) });
          return;
        }

        imported.push(rowData);
      });

      const rowsRead = dataLines.filter((line) => {
        const vals = parseCSVLine(line);
        const rd: Record<string, string> = {};
        columnMap.forEach((k, ci) => { if (k) rd[k] = (vals[ci] || "").trim(); });
        return !isEmptyRow(rd);
      }).length;

      setResult({
        rowsRead,
        imported: imported.length,
        failed: errors.length,
        errors,
        products: imported,
      });
      setStep("results");
    };

    reader.onerror = () => {
      setResult({
        rowsRead: 0,
        imported: 0,
        failed: 1,
        errors: [{ row: 0, message: t("csvImporter.readFailed") }],
        products: [],
      });
      setStep("results");
    };

    reader.readAsText(file, "UTF-8");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleConfirmImport = () => {
    if (result && result.products.length > 0) {
      onImport(result.products);
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose}></div>
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-background-50 rounded-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200/60 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-50 flex items-center justify-center">
              <i className="ri-file-excel-2-line text-primary-600 text-base"></i>
            </div>
            <div>
              <p className="text-foreground-950 font-semibold text-sm">{t("csvImporter.title")}</p>
              <p className="text-foreground-400 text-xs">{t("csvImporter.subtitle")}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Upload Step */}
          {step === "upload" && (
            <div className="space-y-6">
              {/* Expected Columns */}
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                  <i className="ri-table-line text-foreground-500"></i>
                  {t("csvImporter.expectedColumns")}
                </h4>
                <div className="rounded-xl border border-background-200/60 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-background-100/60">
                          <th className="text-left px-4 py-2.5 font-medium text-foreground-600">{t("csvImporter.column")}</th>
                          <th className="text-left px-4 py-2.5 font-medium text-foreground-600">{t("csvImporter.required")}</th>
                          <th className="text-left px-4 py-2.5 font-medium text-foreground-600">{t("csvImporter.example")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {PRODUCT_CSV_FIELDS.map((f, i) => (
                          <tr key={f.key} className={`${i % 2 === 0 ? "bg-background-50" : "bg-background-100/30"}`}>
                            <td className="px-4 py-2.5 text-foreground-900 font-medium">{f.label}</td>
                            <td className="px-4 py-2.5">
                              {f.required ? (
                                <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-medium">{t("csvImporter.requiredBadge")}</span>
                              ) : (
                                <span className="text-xs text-foreground-400">{t("csvImporter.optionalBadge")}</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-foreground-500 font-mono text-[11px]">{f.example}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Validation Rules */}
              <div className="rounded-xl border border-background-200/60 bg-background-100/30 p-4">
                <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2">
                  <i className="ri-shield-check-line text-foreground-500"></i>
                  {t("csvImporter.validationTitle")}
                </h4>
                <ul className="space-y-1.5 text-xs text-foreground-600">
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5 shrink-0">&#8226;</span>
                    <span dangerouslySetInnerHTML={{ __html: t("csvImporter.valRequired") }} />
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground-400 mt-0.5 shrink-0">&#8226;</span>
                    {t("csvImporter.valEmptyRows")}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground-400 mt-0.5 shrink-0">&#8226;</span>
                    {t("csvImporter.valPrice")}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground-400 mt-0.5 shrink-0">&#8226;</span>
                    {t("csvImporter.valDuplicates")}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground-400 mt-0.5 shrink-0">&#8226;</span>
                    {t("csvImporter.valEncoding")}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-foreground-400 mt-0.5 shrink-0">&#8226;</span>
                    {t("csvImporter.valTrim")}
                  </li>
                </ul>
              </div>

              {/* Upload Area */}
              <div>
                <button
                  onClick={generateSampleCsv}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer flex items-center gap-1.5 mb-3"
                >
                  <i className="ri-download-line text-sm"></i>
                  {t("csvImporter.downloadTemplate")}
                </button>

                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-all ${
                    dragOver
                      ? "border-primary-400 bg-primary-50/30"
                      : "border-background-200 hover:border-primary-300 hover:bg-primary-50/20"
                  }`}
                >
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                    <i className="ri-upload-cloud-2-line text-foreground-500 text-2xl"></i>
                  </div>
                  <p className="text-foreground-800 font-medium text-sm mb-1">
                    {dragOver ? t("csvImporter.dropHere") : t("csvImporter.clickToUpload")}
                  </p>
                  <p className="text-foreground-400 text-xs">{t("csvImporter.csvOnly")}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Validating Step */}
          {step === "validating" && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-loader-4-line animate-spin text-primary-500 text-2xl"></i>
              </div>
              <p className="text-foreground-800 font-medium text-sm mb-1">{t("csvImporter.processing")}</p>
              <p className="text-foreground-400 text-xs">{t("csvImporter.validating")}</p>
            </div>
          )}

          {/* Results Step */}
          {step === "results" && result && (
            <div className="space-y-5">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-background-200/60 bg-background-50 p-4 text-center">
                  <p className="text-2xl font-bold text-foreground-950">{result.rowsRead}</p>
                  <p className="text-xs text-foreground-500 mt-1">{t("csvImporter.rowsRead")}</p>
                </div>
                <div className="rounded-xl border border-background-200/60 bg-emerald-50/50 p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{result.imported}</p>
                  <p className="text-xs text-emerald-600 mt-1">{t("csvImporter.imported")}</p>
                </div>
                <div className={`rounded-xl border p-4 text-center ${result.failed > 0 ? "border-background-200/60 bg-red-50/50" : "border-background-200/60 bg-background-50"}`}>
                  <p className={`text-2xl font-bold ${result.failed > 0 ? "text-red-600" : "text-foreground-400"}`}>{result.failed}</p>
                  <p className={`text-xs mt-1 ${result.failed > 0 ? "text-red-600" : "text-foreground-500"}`}>{t("csvImporter.failed")}</p>
                </div>
              </div>

              {/* Error Details */}
              {result.errors.length > 0 && (
                <div className="rounded-xl border border-red-200/60 bg-red-50/30 overflow-hidden">
                  <div className="px-4 py-3 border-b border-red-100 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-500 text-sm"></i>
                    <p className="text-sm font-semibold text-red-700">{t("csvImporter.errorsTitle", { count: result.errors.length })}</p>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {result.errors.map((err, i) => (
                      <div
                        key={i}
                        className={`px-4 py-2.5 flex items-start gap-2.5 text-xs ${
                          i % 2 === 0 ? "bg-red-50/40" : "bg-red-50/20"
                        }`}
                      >
                        <span className="text-red-400 font-mono shrink-0 mt-0.5">
                          {err.row > 0 ? t("csvImporter.rowError", { row: err.row }) : t("csvImporter.fileError")}
                        </span>
                        <span className="text-red-700">{err.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Success Products Preview */}
              {result.products.length > 0 && (
                <div className="rounded-xl border border-emerald-200/60 bg-emerald-50/30 p-4">
                  <p className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-emerald-500"></i>
                    {t("csvImporter.readyTitle", { count: result.products.length })}
                  </p>
                  <div className="max-h-36 overflow-y-auto space-y-1">
                    {result.products.map((p, i) => (
                      <div key={i} className="text-xs text-emerald-700 flex items-center gap-2">
                        <span className="text-emerald-400">{i + 1}.</span>
                        <span className="font-medium">{p.product_name}</span>
                        {p.category && <span className="text-emerald-500">· {p.category}</span>}
                        {p.price && <span className="text-emerald-500">· ${p.price}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === "results" && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-background-200/60 shrink-0 bg-background-50">
            <button
              onClick={resetState}
              className="px-4 py-2.5 rounded-xl border border-background-200 text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
            >
              {t("csvImporter.uploadAnother")}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={handleClose}
                className="px-5 py-2.5 rounded-xl text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
              >
                {t("csvImporter.cancel")}
              </button>
              {result && result.products.length > 0 && (
                <button
                  onClick={handleConfirmImport}
                  className="px-6 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-check-line text-sm"></i>
                  {t("csvImporter.importBtn", { count: result.products.length, plural: result.products.length !== 1 ? "s" : "" })}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}